import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Select,
  DatePicker,
  Modal,
  InputNumber,
  Form,
  Alert,
  Row,
  Col,
  Tooltip,
} from "antd";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import moment from "moment";
import { debounce } from "lodash";
import {
  CardTitle,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  fmtMoney,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import notify from "../utils/notify";

const { Option } = Select;
const { RangePicker } = DatePicker;
const money = (currency, value) => fmtMoney(currency || "BWP", Number(value ?? 0));

const RoomPricePage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const [roomTypes, setRoomTypes] = useState([]);
  const [prices, setPrices] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const currencyWatch = Form.useWatch("currency", form) || "BWP";
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [activeDiscounts, setActiveDiscounts] = useState([]);

  useEffect(() => {
    if (authData?.hotelId) {
      fetchRoomTypes();
      debouncedFetchPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId]);

  useEffect(() => {
    const interval = setInterval(checkDiscountExpiry, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const checkDiscountExpiry = () => {
    const now = moment();
    const expiredDiscounts = prices.filter(
      (price) =>
        price.discount > 0 &&
        price.discountEnd &&
        moment(price.discountEnd).isBefore(now)
    );

    if (expiredDiscounts.length > 0) {
      expiredDiscounts.forEach((price) => {
        handleDiscountExpiry(price.roomPriceId);
      });
    }
  };

  const handleDiscountExpiry = async (priceId) => {
    try {
      await axiosInstance.put(`/Price/${priceId}/expire-discount`);
      fetchPrices();
      notify.info({
        message: "Discount Expired",
        description: "A scheduled discount has been automatically reset",
      });
    } catch (error) {
      console.error("Error expiring discount:", error);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await axiosInstance.get(
        `/RoomTypes/GetRoomTypes/${authData.hotelId}`
      );
      setRoomTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      notify.error({
        message: "Error",
        description: "Failed to fetch room types",
      });
    }
  };

  const debouncedFetchPrices = debounce(async () => {
    try {
      setFetchingPrices(true);
      const response = await axiosInstance.get(
        `/Price/hotel/${authData.hotelId}`
      );
      const pricesData = response.data || [];
      setPrices(pricesData);

      const now = moment();
      const active = pricesData.filter(
        (price) =>
          price.discount > 0 &&
          price.discountStart &&
          price.discountEnd &&
          moment(price.discountStart).isBefore(now) &&
          moment(price.discountEnd).isAfter(now)
      );
      setActiveDiscounts(active);
    } catch (error) {
      if (error.response?.status !== 404) {
        notify.error({
          message: "Error",
          description: "Failed to fetch prices",
        });
      }
      setPrices([]);
    } finally {
      setFetchingPrices(false);
    }
  }, 300);

  const fetchPrices = () => {
    debouncedFetchPrices();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const priceData = {
        ...values,
        hotelId: authData.hotelId,
        roomTypeId: parseInt(values.roomTypeId),
        vatPercentage: values.vatPercentage || 0, // blank = no VAT
        discount: values.discount || 0,
        additionalLevies: values.additionalLevies || 0, // absolute amount
        discountStart: values.discountPeriod?.[0]?.toISOString(),
        discountEnd: values.discountPeriod?.[1]?.toISOString(),
      };

      if (editingPriceId) {
        await axiosInstance.put(`/Price/${editingPriceId}`, priceData);
        notify.success({
          message: "Success",
          description: "Price updated successfully",
        });
      } else {
        await axiosInstance.post("/Price", priceData);
        notify.success({
          message: "Success",
          description: "Price created successfully",
        });
      }

      form.resetFields();
      setEditingPriceId(null);
      setPriceModalOpen(false);
      fetchPrices();
    } catch (error) {
      console.error("Error saving price:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (price) => {
    form.setFieldsValue({
      ...price,
      roomTypeId: price.roomTypeId.toString(),
      discountPeriod: [
        price.discountStart ? moment(price.discountStart) : null,
        price.discountEnd ? moment(price.discountEnd) : null,
      ],
    });
    setEditingPriceId(price.roomPriceId);
    setPriceModalOpen(true);
  };

  const openAdd = () => {
    form.resetFields();
    setEditingPriceId(null);
    setPriceModalOpen(true);
  };

  const closePriceModal = () => {
    setPriceModalOpen(false);
    form.resetFields();
    setEditingPriceId(null);
  };

  const handleDelete = async (priceId) => {
    try {
      await axiosInstance.delete(`/Price/${priceId}`);
      notify.success({
        message: "Success",
        description: "Price deleted successfully",
      });
      fetchPrices();
    } catch (error) {
      notify.error({
        message: "Error",
        description: "Failed to delete price",
      });
    }
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find((rt) => rt.roomTypeId === roomTypeId);
    return roomType?.typeName || "Unknown";
  };

  const getDiscountStatus = (price) => {
    if (!price.discount || price.discount <= 0) return null;

    const now = moment();
    const start = moment(price.discountStart);
    const end = moment(price.discountEnd);

    if (!start.isValid() || !end.isValid()) return <StatusTag status="noshow">Invalid period</StatusTag>;
    if (now.isBefore(start)) return <StatusTag status="pending">Scheduled</StatusTag>;
    if (now.isAfter(end)) return <StatusTag status="cancelled">Expired</StatusTag>;
    return <StatusTag status="available">Active</StatusTag>;
  };

  // --- Price math helpers (per-night) ---
  const discounted = (base, discPct) => base * (1 - (discPct || 0) / 100);
  const vatAmount = (net, vatPct) => net * ((vatPct || 0) / 100);
  // additionalLevies is an ABSOLUTE currency amount per night

  const totalPerNight = (r) => {
    const net = discounted(r.basePrice || 0, r.discount || 0);
    return net + vatAmount(net, r.vatPercentage || 0) + (r.additionalLevies || 0);
  };

  const columns = [
    {
      title: "Room type",
      dataIndex: "roomTypeId",
      key: "roomType",
      fixed: "left",
      width: 170,
      render: (id) => <span className="hms-cell-main">{getRoomTypeName(id)}</span>,
    },
    {
      title: "Base price",
      dataIndex: "basePrice",
      key: "basePrice",
      align: "right",
      render: (price, record) => money(record.currency, price),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (discount, record) =>
        discount > 0 ? (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{discount}%</span>
            <span>{getDiscountStatus(record)}</span>
          </div>
        ) : (
          <span className="hms-muted">None</span>
        ),
    },
    {
      title: "Discount period",
      key: "discountPeriod",
      render: (_, record) =>
        record.discountStart && record.discountEnd ? (
          <span>
            {moment(record.discountStart).format("DD MMM")} to {moment(record.discountEnd).format("DD MMM YYYY")}
          </span>
        ) : (
          <span className="hms-muted">—</span>
        ),
    },
    {
      title: "VAT",
      dataIndex: "vatPercentage",
      key: "vatPercentage",
      align: "right",
      render: (vat) => (Number(vat) > 0 ? `${vat}%` : <span className="hms-muted">No VAT</span>),
    },
    {
      title: "Levies",
      dataIndex: "additionalLevies",
      key: "additionalLevies",
      align: "right",
      render: (levy, record) => money(record.currency, levy || 0),
    },
    {
      title: (
        <Tooltip title="Base price after discount, plus VAT, plus levies">
          <span>Total / night</span>
        </Tooltip>
      ),
      key: "totalPerNight",
      align: "right",
      render: (_, r) => <span className="hms-cell-main">{money(r.currency, totalPerNight(r))}</span>,
    },
    {
      title: "",
      key: "action",
      fixed: "right",
      align: "right",
      width: 110,
      render: (_, record) => (
        <RowActions
          actions={[
            { key: "edit", label: "Edit price", icon: "edit", onClick: () => handleEdit(record) },
            {
              key: "delete",
              label: "Delete price",
              icon: "delete",
              danger: true,
              confirm: "Delete this price?",
              okText: "Delete",
              onClick: () => handleDelete(record.roomPriceId),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="hms-page rooms-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={openAdd} disabled={roomTypes.length === 0}>
              Add price
            </Button>
          </>
        }
      />

      {roomTypes.length === 0 ? (
        <SectionCard>
          <Alert
            type="warning"
            showIcon
            message={
              <span>
                Before setting prices, please <Link to="/hotels/roomtypespage">create room types</Link>.
              </span>
            }
          />
        </SectionCard>
      ) : (
        <SectionCard
          flush
          title={<CardTitle count={prices.length}>Room prices</CardTitle>}
          extra={
            <ListToolbar onRefresh={fetchPrices} loading={fetchingPrices}>
              {activeDiscounts.length > 0 && (
                <StatusTag status="available">
                  {activeDiscounts.length} active discount{activeDiscounts.length === 1 ? "" : "s"}
                </StatusTag>
              )}
            </ListToolbar>
          }
        >
          <Table
            className="hms-table"
            columns={columns}
            dataSource={prices}
            rowKey="roomPriceId"
            pagination={tablePagination("price")}
            scroll={{ x: 1100 }}
            loading={fetchingPrices}
            locale={{
              emptyText: (
                <div className="hms-empty">
                  No prices yet.{" "}
                  <Button type="link" onClick={openAdd}>
                    Add the first one
                  </Button>
                </div>
              ),
            }}
          />
        </SectionCard>
      )}

      <Modal
        title={editingPriceId ? "Edit price" : "Add price"}
        open={priceModalOpen}
        onCancel={closePriceModal}
        onOk={handleSubmit}
        okText={editingPriceId ? "Save changes" : "Add price"}
        confirmLoading={loading}
        width={680}
        forceRender
      >
        <Form form={form} layout="vertical" className="price-form">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="roomTypeId"
                label="Room type"
                rules={[{ required: true, message: "Please select a room type" }]}
              >
                <Select placeholder="Select room type">
                  {roomTypes.map((type) => (
                    <Option key={type.roomTypeId} value={type.roomTypeId.toString()}>
                      {type.typeName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="currency"
                label="Currency"
                initialValue="BWP"
                rules={[{ required: true, message: "Please select currency" }]}
              >
                <Select>
                  <Option value="BWP">BWP (Pula)</Option>
                  <Option value="USD">USD ($)</Option>
                  <Option value="EUR">EUR (€)</Option>
                  <Option value="GBP">GBP (£)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="basePrice"
                label="Base price (per night)"
                rules={[{ required: true, message: "Please enter base price" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  formatter={(value) => `${currencyWatch} ${value}`}
                  parser={(value) => value.replace(/[^0-9.]/g, "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="vatPercentage"
                label="VAT % (optional)"
                tooltip="Leave blank if you do not charge VAT. If you enter a rate it is added to the price."
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="No VAT"
                  formatter={(value) => (value === undefined || value === null || value === "" ? "" : `${value}%`)}
                  parser={(value) => (value ? value.replace("%", "") : "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="additionalLevies" label="Additional levies (per night)" initialValue={0}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  formatter={(value) => `${currencyWatch} ${value}`}
                  parser={(value) => value.replace(/[^0-9.]/g, "")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="discount" label="Discount %" tooltip="Enter 0 to remove the discount">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  max={100}
                  step={0.01}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="discountPeriod"
            label="Discount valid for"
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (getFieldValue("discount") > 0 && (!value || !value[0] || !value[1])) {
                    return Promise.reject(new Error("Please select discount period"));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <RangePicker
              style={{ width: "100%" }}
              disabledDate={(current) => current && current < moment().startOf("day")}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Room pricing guide"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        width={560}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
      >
        <p className="hms-modal-note">
          Set a nightly price for each room type, with VAT, levies and optional time-limited discounts.
        </p>
        <Section title="How it works">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Total per night = base price after discount, plus VAT (only if you set a VAT %), plus levies.</li>
            <li>A discount only applies between its start and end dates.</li>
            <li>Expired discounts are reset automatically (checked every minute).</li>
            <li>Prices can be set in BWP, USD, EUR or GBP.</li>
          </ul>
        </Section>
      </Modal>
    </div>
  );
};

export default RoomPricePage;
